import { ChannelIcon, SectionBoundary, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, Briefcase, Building2, Handshake, MessagesSquare, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { handleLabel } from '../crm/handles';
import { api } from '../lib/api';
import { IDENTITY_CHANNEL } from '../pages/People';
import type { PersonDetail } from '../pages/Person';
import { ConversationOrigin, PurchasesPanel } from '../retail/Purchases';
import type { ConversationDetail } from './types';

/** Who the agent is talking to: handles, companies and anything open with them, beside the conversation. */
export function CustomerPanel({ personId, conversationId }: { personId: string; conversationId: string }) {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const ar = i18n.language === 'ar' || i18n.language === 'ur';
  const q = useQuery({
    queryKey: ['person', personId],
    queryFn: () => api<PersonDetail>(`/v1/people/${personId}`),
  });
  // Shares the thread's query (same key), so this is not a second request.
  const conv = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api<ConversationDetail>(`/v1/conversations/${conversationId}`),
  });
  if (!q.data) return null;
  const { person, identities, accounts, cases, deals, conversations } = q.data;
  const openCases = cases.filter((c) => !['resolved', 'closed'].includes(c.status));
  const openDeals = deals.filter((d) => d.status === 'open');
  const otherConversations = conversations.filter((c) => c.id !== conversationId).slice(0, 5);

  return (
    <section className="card customer-panel" aria-label={t('inbox.customer')} data-testid="customer-panel">
      <div className="card-head">
        <div style={{ minWidth: 0 }}>
          <h2 dir="auto" className="ellipsis">
            {person.displayName}
          </h2>
          {(person.jobTitle || person.city) && (
            <div className="faint ellipsis" style={{ fontSize: 'var(--t-xs)' }} dir="auto">
              {[person.jobTitle, person.city].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <Link to={`/people/${person.id}`} className="btn btn-ghost btn-sm">
          <UserRound size={14} aria-hidden="true" />
          {t('inbox.openProfile')}
        </Link>
      </div>

      {conv.data?.origin && (
        <div className="cp-section">
          <ConversationOrigin origin={conv.data.origin} />
        </div>
      )}

      <SectionBoundary resetKey={personId}>
        <PurchasesPanel
          personId={personId}
          conversationId={conversationId}
          channel={conv.data?.conversation.channel}
          compact
        />
      </SectionBoundary>

      <div className="cp-section">
        <h3>{t('crm.person.identities')}</h3>
        <ul className="list-plain cp-list">
          {identities.map((i) => (
            <li key={i.id}>
              <span className="channel-badge">
                <ChannelIcon channel={IDENTITY_CHANNEL[i.type] ?? 'simulator'} size={12} />
              </span>
              <span className={`grow ellipsis${i.type === 'web_user_id' ? '' : ' mono'}`} dir="auto">
                {handleLabel(t, i.type, i.value)}
              </span>
              {i.verified && (
                <span title={t('crm.person.verified')} aria-label={t('crm.person.verified')}>
                  <BadgeCheck size={13} color="var(--success)" />
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {accounts.length > 0 && (
        <div className="cp-section">
          <h3>{t('crm.person.companies')}</h3>
          <ul className="list-plain cp-list">
            {accounts.map((a) => (
              <li key={a.id}>
                <Building2 size={13} className="faint" aria-hidden="true" />
                <Link to={`/companies/${a.id}`} className="grow ellipsis" dir="auto">
                  {ar && a.nameAr ? a.nameAr : a.name}
                </Link>
                {a.role && <span className="faint">{a.role}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="cp-section">
        <h3>{t('inbox.openWork')}</h3>
        <ul className="list-plain cp-list">
          {openCases.map((c) => (
            <li key={c.id}>
              <Briefcase size={13} className="faint" aria-hidden="true" />
              <Link to={`/cases/${c.id}`} className="grow ellipsis" dir="auto">
                <span className="mono">{c.number}</span> {c.subject}
              </Link>
              <span
                className={`badge ${c.priority === 'urgent' || c.priority === 'high' ? 'badge-danger' : 'badge-neutral'}`}
              >
                {t(`crm.priority.${c.priority}`, { defaultValue: c.priority })}
              </span>
            </li>
          ))}
          {openDeals.map((d) => (
            <li key={d.id}>
              <Handshake size={13} className="faint" aria-hidden="true" />
              <span className="grow ellipsis" dir="auto">
                {d.title}
              </span>
              <span className="faint">{fmt.money(Number(d.amountMinor), d.currency)}</span>
            </li>
          ))}
          {!openCases.length && !openDeals.length && <li className="faint">{t('inbox.nothingOpen')}</li>}
        </ul>
      </div>

      {otherConversations.length > 0 && (
        <div className="cp-section">
          <h3>{t('inbox.otherConversations')}</h3>
          <ul className="list-plain cp-list">
            {otherConversations.map((c) => (
              <li key={c.id}>
                <MessagesSquare size={13} className="faint" aria-hidden="true" />
                <Link to={`/inbox?c=${c.id}`} className="grow">
                  {t(`channels.${c.channel}`)} · {t(`inbox.status.${c.status}`, { defaultValue: c.status })}
                </Link>
                {c.lastMessageAt && <span className="faint">{fmt.relative(c.lastMessageAt)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
